import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import './SubmissionFeedback.css';

interface SubmissionResult {
  passed?: boolean;
  pointsEarned?: number;
  output?: string;
  error?: string | null;
}

interface SubmissionFeedbackProps {
  result?: SubmissionResult | null;
  /** Seconds left before the student may try again (0 when no pause is running). */
  cooldownSeconds?: number;
  /** True once the last allowed attempt was used and the exercise is closed. */
  attemptsExhausted?: boolean;
}

function failureTitle(attemptsExhausted: boolean, t: TFunction): string {
  return t(attemptsExhausted ? 'exercise.failedExhausted' : 'exercise.failedTitle');
}

function failureHint(attemptsExhausted: boolean, cooldownSeconds: number, t: TFunction): string | null {
  if (attemptsExhausted) {
    return null;
  }
  return cooldownSeconds > 0
    ? t('exercise.retryCountdown', { seconds: cooldownSeconds })
    : t('exercise.retryLastChance');
}

function FeedbackTitle({ passed, attemptsExhausted }: { passed: boolean; attemptsExhausted: boolean }) {
  const { t } = useTranslation();
  return (
    <h2 className="submission-feedback__title">
      <span className="submission-feedback__icon" aria-hidden="true">
        {passed ? '✔' : '✘'}
      </span>
      {passed ? t('exercise.passedTitle') : failureTitle(attemptsExhausted, t)}
    </h2>
  );
}

function FeedbackMessage({
  passed,
  points,
  cooldownSeconds,
  attemptsExhausted,
}: {
  passed: boolean;
  points: number;
  cooldownSeconds: number;
  attemptsExhausted: boolean;
}) {
  const { t } = useTranslation();
  if (passed) {
    return <p className="submission-feedback__points">{t('exercise.passedPoints', { points })}</p>;
  }
  const hint = failureHint(attemptsExhausted, cooldownSeconds, t);
  return hint ? <p className="submission-feedback__hint">{hint}</p> : null;
}

function FeedbackDetails({ result }: { result: SubmissionResult }) {
  const details = result.error ?? result.output;
  return details ? <pre className="submission-feedback__details">{details}</pre> : null;
}

/**
 * A long exercise can leave the banner above the viewport: bring each new verdict into view.
 * `changeKey` must not include the ticking countdown, or the page would keep being pulled back.
 */
function useBringIntoView(shown: boolean, changeKey: string) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (shown) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [shown, changeKey]);
  return ref;
}

/** Large, unambiguous verdict banner: colour, icon and text all say whether the answer was right. */
function SubmissionFeedback({ result, cooldownSeconds = 0, attemptsExhausted = false }: SubmissionFeedbackProps) {
  const passed = Boolean(result?.passed);
  const bannerRef = useBringIntoView(Boolean(result), `${passed}-${attemptsExhausted}`);

  if (!result) {
    return null;
  }

  return (
    <section
      ref={bannerRef}
      className={`submission-feedback submission-feedback--${passed ? 'passed' : 'failed'}`}
      role={passed ? 'status' : 'alert'}
      aria-live={passed ? 'polite' : 'assertive'}
    >
      <FeedbackTitle passed={passed} attemptsExhausted={attemptsExhausted} />
      <FeedbackMessage
        passed={passed}
        points={result.pointsEarned ?? 0}
        cooldownSeconds={cooldownSeconds}
        attemptsExhausted={attemptsExhausted}
      />
      <FeedbackDetails result={result} />
    </section>
  );
}

export default SubmissionFeedback;
